import type { RunningWorkoutPlan, WorkoutPlan } from '@/utils/workoutImporter';

export const ARNOLD_RUN_PLANS_KEY = 'runningCoachImportedPlans';
export const CHAT_DEFER_CONTEXT_REFRESH_KEY = 'chatDeferContextRefresh';
export const PENDING_CHAT_AUTO_SEND_KEY = 'pendingChatAutoSend';

export type ArnoldRunPlanEntry = {
  plan: RunningWorkoutPlan;
  /** ISO timestamp when Arnold suggested this run (chat message time). */
  suggestedAt: string;
};

export type ArnoldRunPlanInput = {
  plan: RunningWorkoutPlan;
  suggestedAt?: string;
};

const planFingerprint = (plan: RunningWorkoutPlan): string => {
  const intervals = (plan.intervals || [])
    .map((it) =>
      [
        it.repeat ?? 1,
        it.distance_km ?? '',
        it.duration_minutes ?? '',
        it.pace ?? '',
        it.rest_minutes ?? '',
      ].join(':'),
    )
    .join('|');
  return [
    (plan.title || '').trim().toLowerCase(),
    plan.warmup_minutes ?? '',
    plan.cooldown_minutes ?? '',
    plan.total_distance_km ?? '',
    plan.estimated_duration_minutes ?? '',
    intervals,
    (plan.notes || '').trim().toLowerCase(),
  ].join('::');
};

const sanitizePlan = (raw: unknown): RunningWorkoutPlan | null => {
  if (!raw || typeof raw !== 'object') return null;
  const plan = raw as RunningWorkoutPlan;
  const intervals = Array.isArray(plan.intervals) ? plan.intervals : [];
  if (!plan.title && intervals.length === 0 && !plan.warmup_minutes && !plan.cooldown_minutes && !plan.notes) {
    return null;
  }
  return { ...plan, type: 'running_workout', intervals };
};

const sortEntriesNewestFirst = (entries: ArnoldRunPlanEntry[]): ArnoldRunPlanEntry[] =>
  [...entries].sort(
    (a, b) => new Date(b.suggestedAt || 0).getTime() - new Date(a.suggestedAt || 0).getTime(),
  );

export const isRunningWorkoutPlan = (plan: WorkoutPlan | null | undefined): plan is RunningWorkoutPlan =>
  plan?.type === 'running_workout';

export const isRunningWorkoutCollection = (
  plan: WorkoutPlan | null | undefined,
): plan is { type: 'running_workouts'; workouts: RunningWorkoutPlan[] } => plan?.type === 'running_workouts';

export const mergeArnoldRunPlanEntries = (...batches: ArnoldRunPlanEntry[][]): ArnoldRunPlanEntry[] => {
  const byKey = new Map<string, ArnoldRunPlanEntry>();
  for (const batch of batches) {
    for (const entry of batch) {
      const key = planFingerprint(entry.plan);
      const prev = byKey.get(key);
      if (
        !prev ||
        new Date(entry.suggestedAt || 0).getTime() > new Date(prev.suggestedAt || 0).getTime()
      ) {
        byKey.set(key, entry);
      }
    }
  }
  return sortEntriesNewestFirst([...byKey.values()]);
};

export const loadArnoldRunPlans = (): ArnoldRunPlanEntry[] => {
  try {
    const raw = localStorage.getItem(ARNOLD_RUN_PLANS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];

    const entries: ArnoldRunPlanEntry[] = [];
    for (const item of parsed) {
      if (!item || typeof item !== 'object') continue;

      // New format: { plan, suggestedAt }
      if ('plan' in item && item.plan) {
        const plan = sanitizePlan(item.plan);
        if (plan) {
          entries.push({
            plan,
            suggestedAt: typeof item.suggestedAt === 'string' ? item.suggestedAt : '',
          });
        }
        continue;
      }

      // Legacy format: bare RunningWorkoutPlan object
      const plan = sanitizePlan(item);
      if (plan) {
        entries.push({ plan, suggestedAt: '' });
      }
    }

    return sortEntriesNewestFirst(entries);
  } catch {
    return [];
  }
};

export const saveArnoldRunPlans = (entries: ArnoldRunPlanEntry[]): ArnoldRunPlanEntry[] => {
  const sorted = sortEntriesNewestFirst(entries);
  localStorage.setItem(ARNOLD_RUN_PLANS_KEY, JSON.stringify(sorted));
  return sorted;
};

export const appendArnoldRunPlans = (inputs: ArnoldRunPlanInput[]): ArnoldRunPlanEntry[] => {
  if (inputs.length === 0) return loadArnoldRunPlans();

  const now = new Date().toISOString();
  const incoming: ArnoldRunPlanEntry[] = inputs.map(({ plan, suggestedAt }) => ({
    plan,
    suggestedAt: suggestedAt || now,
  }));

  const merged = mergeArnoldRunPlanEntries(loadArnoldRunPlans(), incoming);
  const existing = loadArnoldRunPlans();
  const existingKeys = new Set(existing.map((entry) => planFingerprint(entry.plan)));
  const changed =
    merged.length !== existing.length ||
    merged.some((entry) => !existingKeys.has(planFingerprint(entry.plan))) ||
    incoming.some((entry) => {
      const prev = existing.find((e) => planFingerprint(e.plan) === planFingerprint(entry.plan));
      return prev?.suggestedAt !== entry.suggestedAt;
    });

  if (!changed) return existing;
  return saveArnoldRunPlans(merged);
};

export const syncArnoldRunPlansFromDetected = (
  detected: Record<string, WorkoutPlan>,
  messageTimestamps: Record<string, string> = {},
): ArnoldRunPlanEntry[] => {
  const toAdd: ArnoldRunPlanInput[] = [];
  for (const [messageId, plan] of Object.entries(detected)) {
    const suggestedAt = messageTimestamps[messageId];
    if (isRunningWorkoutPlan(plan)) {
      toAdd.push({ plan, suggestedAt });
    } else if (isRunningWorkoutCollection(plan)) {
      for (const workout of plan.workouts) {
        toAdd.push({ plan: workout, suggestedAt });
      }
    }
  }
  if (toAdd.length === 0) return loadArnoldRunPlans();
  return appendArnoldRunPlans(toAdd);
};

export const formatArnoldRunSuggestedAt = (iso: string | undefined): string | null => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};
